import { ITranslationResource } from './interfaces';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, retry, shareReplay, tap } from 'rxjs/operators';
import { TranslateLoader } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

export default class MultiTranslateHttpLoader implements TranslateLoader {
  private notificationShown: boolean = false;
  private toastr: ToastrService;

  /**
   * Дедупликация одновременных (in-flight) загрузок одного языка.
   *
   * Словарь одного языка могут запросить параллельно два пути: `translate.use(lang)` через
   * ngx-translate и `TranslationFallbackService.activate()` напрямую. Кэш ngx-translate их не
   * связывает (пустой словарь при сбое сети в кэш не пишется), поэтому без дедупликации язык
   * грузился двумя независимыми сериями HTTP-запросов.
   *
   * Запись удаляется по завершении потока (complete/error): постоянного кэша результата здесь
   * быть не должно — пустой словарь должен оставаться перезапрашиваемым (см. механизм повторной
   * загрузки в `TranslationFallbackService._loadLang`).
   */
  private readonly _inFlightRequests = new Map<string, Observable<any>>();

  constructor(
    private http: HttpClient,
    private resources: ITranslationResource[],
  ) {
    this.toastr = inject(ToastrService);
  }

  public getTranslation(lang: string): Observable<any> {
    const inFlight = this._inFlightRequests.get(lang);
    if (inFlight) {
      return inFlight;
    }

    const requests = this.resources.map((resource) => {
      const path = resource.prefix + lang + resource.suffix;
      return this.http.get(path).pipe(
        tap({
          error: () => {
            if (!this.notificationShown) {
              // Осознанный обход ToastrGuardService для показа критичного уведомления о проблемах с сетью
              ToastrService.prototype.warning.call(this.toastr, 'Соединение не стабильное...');
              this.notificationShown = true;
            }
          },
        }),
        retry({ count: 2, delay: 5000 }),
        catchError((res) => {
          console.error('Something went wrong for the following translation file:', path);
          console.error(res.message);
          return of({});
        }),
      );
    });

    const shared = forkJoin(requests).pipe(
      map((response) => Object.assign({}, ...response)),
      // Запись удаляется по завершении источника (complete/error); отписки подписчиков
      // запись не удаляют — иначе ранняя отписка включила бы дублирующую серию запросов.
      finalize(() => this._inFlightRequests.delete(lang)),
      shareReplay(1),
    );

    this._inFlightRequests.set(lang, shared);
    return shared;
  }
}
